import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, Typography } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSTextField, RMSSelect } from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import personNameSchema from '../../schema/personNameSchema';
import { DEFAULT_VALUES, DICT_SELECT_PROPS } from '../../constants/personNameFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createPersonName, editPersonName } from '../../services/userService';
import { buildFullName } from '../../utility/personNameHelper';

const PersonNameDialog = ({
  open,
  onClose,
  personId,
  name = null,
  nameChangeSnapshot = null,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const isNameChangeMode = Boolean(nameChangeSnapshot);
  const isEditMode = Boolean(name?.ptsNameID);

  const methods = useForm({
    resolver: CreateFormResolver(personNameSchema),
    mode: 'onSubmit',
    defaultValues: { ...DEFAULT_VALUES, ...(nameChangeSnapshot || name || {}) },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const { prefixOptions, suffixOptions, nameTypeOptions } = useUserDictionaryOptions();
  const watchedNameFields = useWatch({
    control: methods.control,
    name: ['Prefix', 'FirstName', 'MiddleName', 'LastName', 'Suffix'],
  });
  const [Prefix, FirstName, MiddleName, LastName, Suffix] = watchedNameFields;
  const livePreview = buildFullName({ Prefix, FirstName, MiddleName, LastName, Suffix });

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsPersonID: personId,
        FirstName: data.FirstName || null,
        MiddleName: data.MiddleName || null,
        LastName: data.LastName || null,
        Prefix: data.Prefix || null,
        Suffix: data.Suffix || null,
        NickName: data.NickName || null,
        Notes: data.Notes || null,
        NameType: data.NameType || null,
        IsDefault: false,
        IsActive: true,
        FullName: buildFullName(data),
        CreatedBy: currentUser?.Username,
        UpdatedBy: currentUser?.Username,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode ? editPersonName(name.ptsNameID, payload) : createPersonName(payload),
        onSuccess: (savedName) => {
          dispatch(
            notify(
              isEditMode ? 'Name updated successfully' : 'Name created successfully',
              'success'
            )
          );
          onSaved?.(savedName);
        },
      });
    },
    [isEditMode, name, personId, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this name. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isNameChangeMode ? 'Save Previous Name' : isEditMode ? 'Edit Name' : 'Add Name'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!name?.Created}
            created={name?.Created}
            createdBy={name?.CreatedBy}
            updated={name?.Updated}
            updatedBy={name?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        {isNameChangeMode ? (
          <Box mb={2}>
            <Typography variant="body2">
              Previous name for this person: <strong>{buildFullName(nameChangeSnapshot)}</strong>{' '}
              will be saved. Select a Type for this previous name.
            </Typography>
          </Box>
        ) : (
          livePreview && (
            <Box mb={2}>
              <Typography variant="h6">{livePreview}</Typography>
            </Box>
          )
        )}

        <Grid container spacing={2}>
          {!isNameChangeMode && (
            <>
              <Grid item xs={4}>
                <RMSSelect
                  name="Prefix"
                  label="Prefix"
                  options={prefixOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField name="FirstName" label="First Name" inputProps={{ maxLength: 100 }} />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField
                  name="MiddleName"
                  label="Middle Name"
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField name="LastName" label="Last Name" inputProps={{ maxLength: 100 }} />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="Suffix"
                  label="Suffix"
                  options={suffixOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField name="NickName" label="Nick Name" inputProps={{ maxLength: 20 }} />
              </Grid>
            </>
          )}

          <Grid item xs={isNameChangeMode ? 12 : 4}>
            <RMSSelect
              name="NameType"
              label="Name Type"
              options={nameTypeOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>

          <Grid item xs={12}>
            <RMSTextField
              name="Notes"
              label="Notes"
              multiline
              rows={4}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonNameDialog;
